import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import {
  AlertTriangleIcon,
  ClockIcon,
  HistoryIcon,
  InboxIcon,
} from "lucide-react";
import type { ReactNode } from "react";

type NotificationHistorySummaryCardsProps = {
  logsTotal: number;
  queueStats: {
    pending: number;
    failed: number;
    total: number;
  };
  isLoading?: boolean;
  activeTab: "logs" | "queue";
  onTabChange: (tab: "logs" | "queue") => void;
  onQueueStatusFilter?: (status: "pending" | "failed" | "all") => void;
};

function SummaryCard({
  title,
  value,
  icon,
  isLoading,
  accentClassName,
  active,
  onClick,
  subtitle,
}: {
  title: string;
  value: number;
  icon: ReactNode;
  isLoading?: boolean;
  accentClassName?: string;
  active?: boolean;
  onClick?: () => void;
  subtitle?: string;
}) {
  const content = (
    <>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="font-medium text-sm">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <span className="text-muted-foreground text-sm">…</span>
        ) : (
          <div className="font-bold text-2xl tabular-nums">
            {value.toLocaleString("lo-LA")}
          </div>
        )}
        {subtitle ? (
          <p className="mt-1 text-muted-foreground text-xs">{subtitle}</p>
        ) : null}
      </CardContent>
    </>
  );

  if (!onClick) {
    return <Card className={cn("gap-0", accentClassName)}>{content}</Card>;
  }

  return (
    <button
      type="button"
      className={cn(
        "text-left transition-colors",
        "rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      )}
      onClick={onClick}
    >
      <Card
        className={cn(
          "gap-0 transition-colors hover:bg-muted/30",
          accentClassName,
          active && "border-primary/50 bg-primary/5 ring-1 ring-primary/30",
        )}
      >
        {content}
      </Card>
    </button>
  );
}

export function NotificationHistorySummaryCards({
  logsTotal,
  queueStats,
  isLoading,
  activeTab,
  onTabChange,
  onQueueStatusFilter,
}: NotificationHistorySummaryCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        title="ປະຫວັດການສົ່ງ"
        value={logsTotal}
        isLoading={isLoading}
        icon={<HistoryIcon className="size-4 text-slate-600" />}
        accentClassName="border-slate-500/20 bg-slate-500/5"
        active={activeTab === "logs"}
        onClick={() => onTabChange("logs")}
        subtitle="ກົດເພື່ອເບິ່ງປະຫວັດ"
      />
      <SummaryCard
        title="ຄິວລໍຖ້າ"
        value={queueStats.pending}
        isLoading={isLoading}
        icon={<ClockIcon className="size-4 text-amber-600" />}
        accentClassName="border-amber-500/20 bg-amber-500/5"
        active={activeTab === "queue"}
        onClick={() => {
          onTabChange("queue");
          onQueueStatusFilter?.("pending");
        }}
        subtitle="ກົດເພື່ອກັ່ນຄິວ"
      />
      <SummaryCard
        title="ຄິວລົ້ມເຫຼວ"
        value={queueStats.failed}
        isLoading={isLoading}
        icon={<AlertTriangleIcon className="size-4 text-destructive" />}
        accentClassName="border-destructive/20 bg-destructive/5"
        onClick={() => {
          onTabChange("queue");
          onQueueStatusFilter?.("failed");
        }}
        subtitle="ກົດເພື່ອກັ່ນຄິວ"
      />
      <SummaryCard
        title="ຄິວທັງໝົດ"
        value={queueStats.total}
        isLoading={isLoading}
        icon={<InboxIcon className="size-4 text-blue-600" />}
        accentClassName="border-blue-500/20 bg-blue-500/5"
        active={activeTab === "queue"}
        onClick={() => {
          onTabChange("queue");
          onQueueStatusFilter?.("all");
        }}
        subtitle="ລາຍການ enqueue ທັງໝົດ"
      />
    </div>
  );
}
